import { useCallback, useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { CalendarClock, Eye, Play, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { ReguaPreviaDialog } from "@/components/whatsapp/ReguaPreviaDialog";
import { ReguaExecutarDialog } from "@/components/whatsapp/ReguaExecutarDialog";

const ETAPAS = [
  { tipo: "AVISO_PREVIO", label: "3 dias antes · evite juros" },
  { tipo: "VENCIMENTO", label: "No dia do vencimento" },
  { tipo: "ATRASO_D1", label: "1 dia de atraso" },
  { tipo: "ATRASO_D5", label: "5 dias de atraso" },
  { tipo: "ATRASO_D10", label: "10 dias de atraso" },
];

export function ReguaAutomaticaCard({ onExecutada }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previaOpen, setPreviaOpen] = useState(false);
  const [execOpen, setExecOpen] = useState(false);

  const load = useCallback(async () => {
    try { setCfg((await api.get("/whatsapp/regua/config")).data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (ativa) => {
    setSaving(true);
    try {
      setCfg((await api.put("/whatsapp/regua/config", { ativa })).data);
      toast.success(ativa ? "Régua automática ATIVADA — envios diários às 09:00" : "Régua automática desativada");
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };

  const ult = cfg?.ultima_execucao;
  return (
    <Card className="p-6 rounded-sm border-2 border-border" data-testid="regua-automatica-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="label-tag flex items-center gap-1.5"><CalendarClock size={12} /> Régua automática</div>
          <h2 className="font-heading font-bold text-xl mt-1">Cobrança diária por WhatsApp + e-mail</h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-xl leading-relaxed">
            Roda todo dia às 09:00 (horário de Brasília). Fora da janela 08h–20h as mensagens aguardam na fila.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge data-testid="regua-status-badge" variant="outline"
            className={`text-[10px] uppercase tracking-widest ${cfg?.ativa ? "border-success/40 text-success bg-success/5" : "text-muted-foreground"}`}>
            {cfg?.ativa ? "Ativada" : "Desligada"}
          </Badge>
          <Switch data-testid="regua-toggle" checked={!!cfg?.ativa} disabled={!cfg || saving} onCheckedChange={toggle} />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-5">
        {ETAPAS.map((e) => (
          <Badge key={e.tipo} variant="outline" className="text-[11px] font-normal rounded-sm py-1">{e.label}</Badge>
        ))}
      </div>

      <div className="flex items-start gap-2 mt-4 text-xs text-muted-foreground">
        <ShieldCheck size={14} className="text-success mt-0.5 flex-shrink-0" />
        <span>Intervalo de 10–15s entre cada mensagem (proteção contra bloqueio) · nunca cobra boleto já pago ·
          em atraso mostra valor original, multa, juros e valor atualizado.</span>
      </div>

      {ult && (
        <div className="mt-4 text-xs font-mono text-muted-foreground" data-testid="regua-ultima-execucao">
          Última execução {new Date(ult.em).toLocaleString("pt-BR")} · {ult.enviados} enviada(s) · {ult.agendados} na fila · {ult.falhas} falha(s) · {ult.abortados_pago} já paga(s)
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-5">
        <Button data-testid="regua-previa-btn" variant="outline" className="rounded-sm" onClick={() => setPreviaOpen(true)}>
          <Eye size={14} className="mr-2" /> Simular envios de hoje
        </Button>
        <Button data-testid="btn-executar-regua-wa" className="rounded-sm bg-foreground text-background hover:bg-foreground/90" onClick={() => setExecOpen(true)}>
          <Play size={14} className="mr-2" /> Executar agora
        </Button>
      </div>

      <ReguaPreviaDialog open={previaOpen} onOpenChange={setPreviaOpen} />
      <ReguaExecutarDialog open={execOpen} onOpenChange={setExecOpen} onDone={() => { load(); onExecutada?.(); }} />
    </Card>
  );
}
